import type { StyleRule } from '@archipro-design/aria';
import {
    pxToRem,
    pxArrayToRem,
    inputSlotClassNames,
} from '@archipro-design/aria';
import { labelClassName } from '@fluentui/react-northstar';

const duration = 0.5;
const waitOnSuccess = 0.5;

export const Wrap: StyleRule = ({ theme }) => ({
    width: pxToRem(320),

    position: `relative`,
    margin: pxArrayToRem([14, 0, 30]),

    [theme.screen.laptop]: {
        marginTop: theme.siteVariables.spaces['space-48'],
        margin: pxArrayToRem([48, 0, 0, 0]),
        height: pxToRem(80),
    },
});

export const TopBar: StyleRule<{
    formInputReveal: string;
    formInputConceal: string;
    showSuccess: boolean;
}> = ({ formInputReveal, formInputConceal, showSuccess, theme }) => ({
    height: pxToRem(40),
    boxSizing: `border-box`,
    width: `100%`,

    animationName: `${showSuccess ? formInputConceal : formInputReveal}`,
    animationDuration: `${duration}s`,
    animationDelay: `${showSuccess ? `${waitOnSuccess}s` : `0`}`,
    animationFillMode: `forwards`,
    animationTimingFunction: `ease`,
    borderBottom: `1px solid`,

    [`& > div`]: {
        height: `inherit`,
        display: 'flex',
    },

    [`& .${inputSlotClassNames.input}`]: {
        height: pxToRem(40),
        width: `100%`,
        textAlign: `center`,
        fontSize: `max(16px, ${pxToRem(16)})`,
        lineHeight: 1,
        boxSizing: `border-box`,
        border: 'none',
        padding: 0,

        // offset for border
        marginTop: `-1px`,
        paddingTop: `1px`,
    },

    [`& .${inputSlotClassNames.input}:-webkit-autofill`]: {
        [`-webkit-box-shadow`]: `0 0 0 30px ${theme.siteVariables.bodyBackground} inset`,
        backgroundClip: 'content-box',
    },
});

export const FormButton: StyleRule = ({ theme }) => ({
    background: 'transparent',
    [`&:hover`]: {
        background: 'transparent',
    },

    // Change font only for mobile
    [theme.screen.max.laptop]: {
        [`& .${labelClassName}`]: {
            ...theme.siteVariables.textStyles.Heading.Roman55,
            textTransform: 'uppercase',
        },
    },

    [theme.screen.laptop]: {
        [`&:hover`]: {
            backgroundColor:
                theme.siteVariables.colors.transparency.black['6%'],
        },
    },
});

export const Subscribed: StyleRule = ({ theme }) => ({
    background: 'transparent',
    pointerEvents: `none`,

    // Change font only for mobile
    [theme.screen.max.laptop]: {
        [`& .${labelClassName}`]: {
            ...theme.siteVariables.textStyles.Heading.Roman55,
            textTransform: 'uppercase',
        },
    },
});

export const FormInputReveal = () => {
    return {
        from: { opacity: 0, height: 0 },
        to: { opacity: 1, height: pxToRem(40) },
    };
};

export const FormInputConceal = () => {
    return {
        from: { opacity: 1, height: pxToRem(40) },
        to: { opacity: 0, height: 0 },
    };
};

export const ErrorState: StyleRule<{
    validationTrigged: boolean;
}> = ({ validationTrigged, theme }) => ({
    position: `absolute`,
    top: 0,
    transform: `translateY(-100%)`,

    width: `100%`,
    textAlign: `center`,
    color: validationTrigged
        ? theme.siteVariables.contextualColors.danger['100']
        : theme.siteVariables.colors.charcoal['100'],

    [theme.screen.laptop]: {
        marginTop: pxToRem(-4),
    },
});
